export interface KwelloSparklineMouseEvent {
  x: number;
  y: number;
  index: number;
}

export interface KwelloSparklineChartProps {
  data: number[];
  width?: number;
  height?: number;
  color: string;
  onMouseEnterPoint?: (params: KwelloSparklineMouseEvent) => void;
  onMouseLeavePoint?: (params: KwelloSparklineMouseEvent) => void;
}

export function KwelloSparklineChart({
  data,
  width = 80,
  height = 30,
  color,
  onMouseEnterPoint,
  onMouseLeavePoint,
}: KwelloSparklineChartProps) {
  // Get min and max values from data for scaling
  const minValue = Math.min(...data);
  const maxValue = Math.max(...data);

  // Scale data to fit within the sparkline's height
  const scaleY = (value: number) =>
    value > 0 ? height - ((value - minValue) / (maxValue - minValue)) * height : 0;

  // Generate points for the sparkline
  const points = data
    .map((value: number, index: number) => {
      const cx = (index / (data.length - 1)) * width;
      const cy = scaleY(value);
      return `${isNaN(cx) ? 0 : cx},${isNaN(cy) ? 0 : cy}`;
    })
    .join(' ');
  const onMouseEnterInternal = (
    event: React.MouseEvent<SVGCircleElement, MouseEvent>,
    index: number
  ) => {
    if (onMouseEnterPoint) {
      onMouseEnterPoint({
        index,
        x: event.pageX,
        y: event.pageY,
      });
    }
  };

  const onMouseLeaveInternal = (
    event: React.MouseEvent<SVGCircleElement, MouseEvent>,
    index: number
  ) => {
    if (onMouseLeavePoint) {
      onMouseLeavePoint({
        index,
        x: event.pageX,
        y: event.pageY,
      });
    }
  };

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      style={{ overflow: 'visible' }}
    >
      {/* Line connecting the points */}
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" />
      {/* Dots at each data point */}
      {data.map((value: number, index: number) => {
        const cx = (index / (data.length - 1)) * width;
        const cy = scaleY(value);
        return (
          <circle
            key={index}
            cx={isNaN(cx) ? 0 : cx}
            cy={isNaN(cy) ? 0 : cy}
            r="3"
            fill={color}
            onMouseEnter={(e: React.MouseEvent<SVGCircleElement, MouseEvent>) =>
              onMouseEnterInternal(e, index)
            }
            onMouseLeave={(e: React.MouseEvent<SVGCircleElement, MouseEvent>) =>
              onMouseLeaveInternal(e, index)
            }
          />
        );
      })}
    </svg>
  );
}
